/**
 * Phone-swap API (ADR 0049): what the scanner-driven installer page calls.
 * Lookup the old phone by MAC, commit old → new (+ serial) at the same or a
 * new location, then — for a moved desk — watch for the 933 call attributed
 * to the new phone and record the read-back. All routes need `devices.swap`.
 */

import { apiFetch } from "./client";

export interface SwapLookupLine {
  line_index: number;
  role: string;
  node_name: string;
  domain: string | null;
  extension: string;
  is_e911_line: boolean;
}

/** A platform device as the installer sees it after scanning its MAC. */
export interface SwapLookup {
  state:
    | "pre_registered"
    | "pending"
    | "adopted"
    | "rejected"
    | "retired"
    | "discovered_only";
  device_id?: number;
  mac: string;
  serial?: string | null;
  label?: string | null;
  vendor?: string;
  model: string | null;
  expansion_modules?: number;
  location_id?: number | null;
  location_name?: string | null;
  e911_state?: "unverified" | "verified";
  attested?: boolean;
  attested_by?: string | null;
  lines?: SwapLookupLine[];
  inventory_extensions?: string[];
  template_name?: string | null;
  device_buttons?: number;
  registering_ip?: string | null;
  /** null = no ranges to check against. */
  registering_in_range?: boolean | null;
  replaced_by_id?: number | null;
  /** discovered_only: the nodes know it, the platform does not yet. */
  name?: string | null;
  extensions?: string[];
  nodes?: string[];
  hint?: string;
}

export interface SwapResult {
  successor_id: number;
  successor_mac: string;
  serial: string | null;
  predecessor_id: number;
  predecessor_mac: string;
  lines: {
    line_index: number;
    extension: string;
    node_id: number;
    rotated: boolean;
  }[];
  buttons_copied: number;
  location_id: number | null;
  location_name: string | null;
  attested: boolean;
  needs_933: boolean;
  notes: string[];
}

export interface SwapEvent {
  id: number;
  observed_at: string | null;
  source: string;
  node: string | null;
  dialed: string | null;
  egress_ok: boolean | null;
  connected: boolean | null;
  location: string | null;
  detail: string | null;
}

export function swapLookup(mac: string): Promise<SwapLookup> {
  return apiFetch(
    `/api/provisioning/swap/lookup?mac=${encodeURIComponent(mac)}`,
  );
}

export function swapDevice(body: {
  old_mac: string;
  new_mac: string;
  serial?: string | null;
  location_unchanged: boolean;
  location_id?: number | null;
}): Promise<SwapResult> {
  return apiFetch("/api/provisioning/swap", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function swapRecord933(
  deviceId: number,
  body: { path: string; result: string; notes?: string },
): Promise<Record<string, unknown>> {
  return apiFetch(`/api/provisioning/swap/${deviceId}/933`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function swapEvents(deviceId: number): Promise<{ items: SwapEvent[] }> {
  return apiFetch(`/api/provisioning/swap/${deviceId}/events`);
}
